import {
  formatIncotermRoute,
  formatParcelSize,
  resolveFobPortLabel,
  getFobLocationNames,
} from './utils';

const isFilled = value =>
  value !== null && value !== undefined && String(value).trim() !== '';

const getGroupId = item =>
  String(
    item.quantityIncotermId ??
      `${item.incoterm ?? ''}-${item.route ?? ''}-${item.id ?? ''}`
  );

const getCargoRatioKey = item =>
  String(
    item.cargoRatioLabel ??
      item.ratioName ??
      item.ratioLpg ??
      item.cargoRatio ??
      '-'
  );

const getParcelSizeSortValue = item => {
  const raw = item.parcelSize ?? item.deliveryBasis;
  const numeric = Number(String(raw ?? '').replace(/[^\d.-]/g, ''));
  return Number.isFinite(numeric) ? numeric : 0;
};

const isFilledParcelLabel = (label, unitLabel = 'MT') => {
  if (!isFilled(label)) return false;
  const text = String(label).trim();
  if (text === '-' || text === `- ${unitLabel}`) return false;
  return !text.startsWith('-');
};

const rowParcelSizeLabel = (item, unitLabel = 'MT') => {
  if (isFilledParcelLabel(item.parcelSizeLabel, unitLabel)) {
    return item.parcelSizeLabel;
  }
  const label = formatParcelSize(
    item.parcelSize ?? item.deliveryBasis,
    unitLabel
  );
  return isFilledParcelLabel(label, unitLabel) ? label : null;
};

const uniqueParcelSizes = (items, unitLabel = 'MT') => {
  const seen = new Set();
  const sizes = [];
  const push = label => {
    if (!isFilledParcelLabel(label, unitLabel) || seen.has(label)) return;
    seen.add(label);
    sizes.push(label);
  };

  items.forEach(item => push(rowParcelSizeLabel(item, unitLabel)));
  if (sizes.length) return sizes;

  const fromDelivery = items.find(
    item => Array.isArray(item.parcelSizeLabels) && item.parcelSizeLabels.length
  )?.parcelSizeLabels;
  (fromDelivery || []).forEach(push);
  return sizes;
};

const resolvePort1 = item =>
  item.port1 || item.loadingPort || resolveFobPortLabel(item) || null;

const resolvePort1Names = item => {
  if (Array.isArray(item.fobLocationNames) && item.fobLocationNames.length) {
    return item.fobLocationNames;
  }
  return getFobLocationNames(item);
};

/**
 * Flatten delivery-detail items into rowspan-aware LPG offer rows.
 * Structure mirrors table.md: Incoterm/Ports/Counter span the group;
 * Cargo Ratio spans parcel-size rows within that ratio.
 */
export const buildLpgOfferTableModel = (items = [], unitLabel = 'MT') => {
  if (!items.length) return [];

  const groups = new Map();

  items.forEach(item => {
    const groupId = getGroupId(item);
    if (!groups.has(groupId)) groups.set(groupId, []);
    groups.get(groupId).push(item);
  });

  const rows = [];

  groups.forEach((groupItems, groupId) => {
    const sorted = [...groupItems].sort((a, b) => {
      const ratioCmp = getCargoRatioKey(a).localeCompare(getCargoRatioKey(b));
      if (ratioCmp !== 0) return ratioCmp;
      return getParcelSizeSortValue(a) - getParcelSizeSortValue(b);
    });

    const groupRowSpan = sorted.length;
    const groupParcelSizes = uniqueParcelSizes(sorted, unitLabel);
    const first = sorted[0];

    const ratioBuckets = new Map();
    sorted.forEach(item => {
      const key = getCargoRatioKey(item);
      if (!ratioBuckets.has(key)) ratioBuckets.set(key, []);
      ratioBuckets.get(key).push(item);
    });

    let offset = 0;
    ratioBuckets.forEach((ratioItems, cargoRatioLabel) => {
      const ratioParcelSizes = uniqueParcelSizes(ratioItems, unitLabel);
      ratioItems.forEach((item, sizeIndex) => {
        const isFirstInGroup = offset === 0;
        const isLastInGroup = offset === groupRowSpan - 1;
        const isFirstInRatio = sizeIndex === 0;

        rows.push({
          groupId,
          data: item,
          cargoRatioLabel: cargoRatioLabel === '-' ? null : cargoRatioLabel,
          showGroupCells: isFirstInGroup,
          isLastInGroup,
          groupRowSpan,
          showCargoRatioCell: isFirstInRatio,
          cargoRatioRowSpan: ratioItems.length,
          parcelSizes: ratioParcelSizes,
          groupMeta: {
            incotermRoute: formatIncotermRoute(first.incoterm, first.route),
            route: first.route,
            port1: resolvePort1(first),
            port1Names: resolvePort1Names(first),
            port2: first.port2,
            port3: first.port3,
            port1StartDate: first.port1StartDate,
            port1EndDate: first.port1EndDate,
            port2StartDate: first.port2StartDate,
            port2EndDate: first.port2EndDate,
            port3StartDate: first.port3StartDate,
            port3EndDate: first.port3EndDate,
            addAld: first.addAld,
            addAld2: first.addAld2,
            addAld3: first.addAld3,
            parcelSizes: groupParcelSizes,
            representative: first,
          },
        });

        offset += 1;
      });
    });
  });

  return rows;
};
